import { useMemo } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";

export const CRM_DEFAULT_LANGUAGE_ID = "en";

const CRM_COPY = {
  en: {
    common: {
      crm: "CRM",
      pilcore: "Pilcore",
      modules: "Modules",
      businessUnit: "Business Unit",
      logout: "Logout",
      noRecords: "No records",
      noLinkedContact: "No linked contact",
      noMessages: "No conversation history yet",
      search: "Search",
      clear: "Clear",
      new: "New",
      edit: "Edit",
      view: "View",
      save: "Save",
      cancel: "Cancel",
      saving: "Saving",
      loading: "Loading",
      optional: "Optional",
      noCompany: "No company",
      noContact: "No contact",
      noLead: "No lead",
      noConversation: "No conversation",
      active: "Active",
      inactive: "Inactive",
      merge: "Merge",
      unmerge: "Unmerge",
      merged: "Merged",
    },
    conversationChannels: {
      manual: "Manual",
      call: "Call",
      whatsapp: "WhatsApp",
      email: "Email",
      meeting: "Meeting",
      note: "Note",
    },
    conversationDirections: {
      internal: "Internal",
      incoming: "Incoming",
      outgoing: "Outgoing",
    },
    conversationMessageTypes: {
      note: "Note",
      text: "Text",
      call: "Call",
      meeting: "Meeting",
      whatsapp: "WhatsApp",
      email: "Email",
    },
    callLog: {
      labels: {
        number: "Number",
        extension: "Extension",
        result: "Result",
        duration: "Duration",
        recordingId: "Recording ID",
      },
      events: {
        NOTIFY_START: "Incoming call started",
        NOTIFY_INTERNAL: "Incoming call routed to extension",
        NOTIFY_ANSWER: "Call answered",
        NOTIFY_END: "Incoming call ended",
        NOTIFY_OUT_START: "Outgoing call started",
        NOTIFY_OUT_END: "Outgoing call ended",
        NOTIFY_RECORD: "Call recording ready",
        NOTIFY_IVR: "IVR option selected",
      },
      dispositions: {
        answered: "Answered",
        busy: "Busy",
        cancel: "Cancelled",
        cancelled: "Cancelled",
        "no answer": "No answer",
        failed: "Failed",
        "call failed": "Failed",
        "no money": "No balance",
        "unallocated number": "Number unavailable",
        "no limit": "Limit reached",
        "no day limit": "Daily limit reached",
        "line limit": "Line limit reached",
        "no money, no limit": "Balance or limit issue",
      },
    },
    leadStages: {
      new: "New",
      qualified: "Qualified",
      proposal: "Proposal",
      won: "Won",
      lost: "Lost",
    },
    taskTypes: {
      follow_up: "Follow-up",
      call: "Call",
      email: "Email",
      whatsapp: "WhatsApp",
      meeting: "Meeting",
      todo: "To-do",
    },
    taskPriorities: {
      low: "Low",
      normal: "Normal",
      high: "High",
      urgent: "Urgent",
    },
    taskStatuses: {
      open: "Open",
      in_progress: "In progress",
      done: "Done",
      cancelled: "Cancelled",
    },
    shell: {
      navigationLabel: "CRM navigation",
      pinSidebar: "Pin sidebar",
      unpinSidebar: "Unpin sidebar",
    },
    nav: {
      dashboard: { label: "Dashboard", meta: "Commercial overview" },
      contacts: { label: "Contacts", meta: "People and relationships" },
      companies: { label: "Companies", meta: "Accounts and organisations" },
      leads: { label: "Leads", meta: "Prospects and qualification" },
      opportunities: { label: "Opportunities", meta: "Deals and pipeline" },
      activity: { label: "Activity", meta: "Calls, emails and notes" },
      tasks: { label: "Tasks", meta: "Follow-ups and ownership" },
      campaigns: { label: "Campaigns", meta: "Segments and outreach" },
      loyalty: { label: "Loyalty", meta: "Guest value and retention" },
      reports: { label: "Reports", meta: "CRM performance" },
      settings: { label: "Settings", meta: "Pipelines and preferences" },
      userExperience: { label: "User Experience" },
      opportunityPipelines: { label: "Pipelines" },
    },
    metrics: {
      contacts: "Contacts",
      companies: "Companies",
      leads: "Leads",
      opportunities: "Opportunities",
      open_tasks: "Open tasks",
      activities_this_week: "Weekly activity",
    },
    dashboard: {
      title: "Dashboard",
      refresh: "Refresh",
      loading: "Loading",
      error: "Could not load the dashboard.",
      metricsAria: "CRM metrics",
      pipelineKicker: "Pipeline",
      opportunitiesTitle: "Opportunities",
      agendaKicker: "Agenda",
      tasksTitle: "Tasks",
      timelineKicker: "Timeline",
      recentActivityTitle: "Recent activity",
      totalValue: "Total value",
      emptyTasks: "No open tasks",
      emptyActivity: "No activity recorded",
      dueAt: "Due",
      openCount: (count) => `${count} open`,
    },
    sections: {
      contacts: {
        title: "Contacts",
        columns: ["Name", "Company", "Email", "Phone", "Status"],
      },
      companies: {
        title: "Companies",
        columns: ["Company", "Type", "Email", "Phone", "Status"],
      },
      leads: {
        title: "Leads",
        columns: ["Lead", "Source", "Owner", "Date", "Status"],
      },
      opportunities: {
        title: "Opportunities",
        columns: ["Opportunity", "Customer", "Stage", "Value", "Close"],
      },
      activity: {
        title: "Activity",
        columns: ["Date", "Type", "Related to", "User", "Detail"],
      },
      tasks: {
        title: "Tasks",
        columns: ["Task", "Related to", "Due date", "Owner", "Status"],
      },
      campaigns: {
        title: "Campaigns",
        columns: ["Campaign", "Segment", "Start", "End", "Status"],
      },
      loyalty: {
        title: "Loyalty",
        columns: ["Customer", "Level", "Last stay", "Value", "Status"],
      },
      reports: {
        title: "Reports",
        columns: ["Report", "Period", "Owner", "Updated", "Status"],
      },
      settings: {
        title: "Settings",
        columns: ["Parameter", "Value", "Updated"],
      },
    },
    entities: {
      contacts: {
        newTitle: "New contact",
        editTitle: "Edit contact",
        searchPlaceholder: "Search contacts by name, email, phone or document...",
        empty: "No contacts found.",
        loadError: "Could not load contacts.",
        saveError: "Could not save contact.",
        table: {
          name: "Name",
          company: "Company",
          email: "Email",
          phone: "Phone",
          status: "Status",
          actions: "Actions",
        },
        fields: {
          first_name: "First name",
          last_name: "Last name",
          email: "Email",
          phone: "Phone",
          company_id: "Company",
          job_title: "Job title",
          department: "Department",
          crm_status: "CRM status",
          source: "Source",
          notes: "Notes",
        },
        pagination: {
          previous: "Previous",
          next: "Next",
          page: (page) => `Page ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} of ${total} contacts` : "0 contacts",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Contact actions",
          noneSelected: "No contact selected",
          selected: (count) => `${count} selected`,
          selectContact: (name) => `Select ${name}`,
          selectVisible: "Select visible contacts",
          singleOnly: "Select one contact for this action",
          mergeMinimum: "Select at least two contacts to merge",
          call: "Call",
          merge: "Merge",
          clear: "Clear",
        },
        merge: {
          title: "Merge contacts",
          subtitle: (count) => `${count} contacts selected`,
          help: "Choose the contact that will remain visible. Empty fields, activity, tasks and linked leads move into it. The absorbed contacts are hidden and can be restored with unmerge.",
          targetLabel: "Final contact",
          targetHint: (id) => `Contact ID ${id}`,
          finalRecord: "Final contact",
          absorbedRecords: "Absorbed contacts",
          confirm: "Merge contacts",
          saving: "Merging",
          cancel: "Cancel",
          error: "Could not merge contacts.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "Could not unmerge contact.",
          historyTitle: "Merge history",
          noHistory: "No merge history yet.",
        },
        detail: {
          viewDetails: "View contact",
          backToContacts: "Contacts",
          refresh: "Refresh",
          loadError: "Could not load contact.",
          untitledContact: "Untitled contact",
          noEmail: "No email",
          noPhone: "No phone",
          noNotes: "No notes yet.",
          noDueDate: "No due date",
          emptyActivity: "No activity for this contact yet.",
          emptyLeads: "No leads linked to this contact yet.",
          emptyOpportunities: "No opportunities linked to this contact yet.",
          emptyTasks: "No tasks for this contact yet.",
          tabsLabel: "Contact detail tabs",
          tabs: {
            overview: "Overview",
            leads: "Leads",
            opportunities: "Opportunities",
            activity: "Activity",
            tasks: "Tasks",
          },
          metrics: {
            activity: "Activity",
            openTasks: "Open tasks",
            lastContact: "Last contact",
          },
          sections: {
            contact: "Contact information",
            company: "Company",
            notes: "Notes",
          },
          fields: {
            lastContacted: "Last contacted",
          },
          unmerge: "Unmerge contact",
        },
      },
      leads: {
        newTitle: "New lead",
        editTitle: "Edit lead",
        searchPlaceholder: "Search leads by title, source or notes...",
        empty: "No leads found.",
        opportunities: {
          newTitle: "New opportunity",
          editTitle: "Edit opportunity",
          view: "View opportunity",
          detailTitle: "Opportunity detail",
          detailLoadError: "Could not load opportunity detail.",
          noItem: "No opportunity",
          fieldTitle: "Opportunity title",
          tableTitle: "Opportunity",
          searchPlaceholder: "Search opportunities by title, source or notes...",
          empty: "No opportunities found.",
          selectionTitle: "Opportunity actions",
          noneSelected: "No opportunity selected",
          selectItem: (title) => `Select ${title}`,
          singleOnly: "Select one opportunity for this action",
          workPermission: "You cannot work on this opportunity",
          assignPermission: "You cannot assign every selected opportunity",
          mergeMinimum: "Select at least two opportunities to merge",
          mergePermission: "You cannot merge every selected opportunity",
          assignmentChange: "Change owner",
          assignmentAssignTitle: "Assign opportunity",
          assignmentMultipleTitle: "Assign selected opportunities",
          assignmentChangeTitle: "Change opportunity owner",
          assignmentItems: "Opportunities",
          selectedItems: (count) => `${count} selected opportunities`,
          assignmentError: "Could not assign opportunity.",
          history: "Assignment history",
          historyError: "Could not load assignment history.",
          tableView: "Table",
          kanbanView: "Pipeline",
          pipeline: "Pipeline",
          pipelineStage: "Pipeline stage",
          noPipeline: "No pipeline",
          allPipelines: "All pipelines",
          pipelineLoadError: "Could not load opportunity pipelines.",
          pipelineEmpty: "No active stages in this pipeline.",
          pipelineUnassignedTitle: "No pipeline",
          moveToStage: "Move to stage",
          noOpportunitiesInStage: "No opportunities here",
          paginationSummary: (start, end, total) =>
            total > 0 ? `${start}-${end} of ${total} opportunities` : "0 opportunities",
          merge: {
            title: "Merge opportunities",
            subtitle: (count) => `${count} opportunities selected`,
            help: "Choose the opportunity that will remain visible. Empty fields, activity and tasks move into it. The absorbed opportunities are hidden and can be restored with unmerge.",
            targetLabel: "Final opportunity",
            targetHint: (id) => `Opportunity ID ${id}`,
            finalRecord: "Final opportunity",
            absorbedRecords: "Absorbed opportunities",
            confirm: "Merge opportunities",
            saving: "Merging",
            cancel: "Cancel",
            error: "Could not merge opportunities.",
            unmerge: "Unmerge",
            merged: "Merged",
            unmerged: "Unmerged",
            unmergeError: "Could not unmerge opportunity.",
            historyTitle: "Merge history",
            noHistory: "No merge history yet.",
          },
        },
        pipeline: {
          tableView: "Table",
          kanbanView: "Pipeline",
          pipeline: "Pipeline",
          pipelineStage: "Pipeline stage",
          noPipeline: "No pipeline",
          allPipelines: "All pipelines",
          pipelineLoadError: "Could not load pipelines.",
          pipelineEmpty: "No active stages in this pipeline.",
          pipelineUnassignedTitle: "No pipeline",
          moveToStage: "Move to stage",
          noItemsInStage: "No leads here",
          itemCount: (count) => `${count} lead${Number(count) === 1 ? "" : "s"}`,
        },
        loadError: "Could not load leads.",
        saveError: "Could not save lead.",
        createContactError: "Could not create contact.",
        createCompanyError: "Could not create company.",
        addContactOption: "+ New contact",
        addCompanyOption: "+ New company",
        relationship: {
          title: "Link contact and company?",
          message: (contact, company) =>
            `${contact} is not linked to ${company}. Do you want to link them now?`,
          currentCompany: (company) => `Current contact company: ${company}.`,
          confirm: "Link them",
          cancel: "Cancel",
          saving: "Linking",
          error: "Could not link the contact to the company.",
        },
        detail: {
          view: "View lead",
          title: "Lead detail",
          loadError: "Could not load lead detail.",
          contacts: "Contacts",
          companies: "Companies",
          noContacts: "No contacts assigned.",
          noCompanies: "No companies assigned.",
          noSource: "No source",
          updated: "Updated",
          primaryContact: "Lead contact",
          companyContact: "Company contact",
          removeContact: "Unassign contact",
          removeCompany: "Unassign company",
          removeContactError: "Could not unassign contact.",
          removeCompanyError: "Could not unassign company.",
          companyContacts: (count) => `${count} contact${Number(count) === 1 ? "" : "s"}`,
        },
        import: {
          bulkOptions: "Bulk options",
          downloadTemplate: "Blank template",
          uploadFile: "Import / preview",
          exportLeads: "Export leads Excel",
          exporting: "Exporting",
          previewing: "Previewing",
          importing: "Importing",
          error: "Could not import the file.",
          exportError: "Could not export leads.",
          emptyFile: "The selected file does not contain data rows.",
          unknownColumns: (columns) => `Unknown columns: ${columns}. Use the template headers.`,
          rowError: (row, column, message) => `Row ${row}, ${column}: ${message}`,
          actions: {
            created: "will be created",
            updated: "will be updated",
            reused: "has no changes",
            unknown: "will be reviewed",
          },
          previewTitle: "Import preview",
          previewSummary: (result) =>
            `${result.rows_processed || 0} ready, ${result.rows_failed || 0} with errors, ${result.total_rows || 0} total.`,
          previewRowsTitle: "Ready rows",
          previewErrorsTitle: "Rows to fix",
          previewRow: (item, action) =>
            `Row ${item.row}: lead ${action}${item.lead_title ? ` - ${item.lead_title}` : ""}`,
          previewMeta: (companyAction, contactAction) =>
            `Company ${companyAction}; contact ${contactAction}.`,
          previewError: (item) => `Row ${item.row}, ${item.column}: ${item.message}`,
          previewEmptyRows: "No rows are ready to import.",
          previewNoErrors: "No row errors found.",
          confirmImport: "Import valid rows",
          cancelPreview: "Cancel",
          moreRows: (count) => `+ ${count} more ready rows`,
          moreErrors: (count) => `+ ${count} more errors`,
          resultErrorsTitle: "Rows still to fix",
          resultErrorsSummary: (count) => `${count} row errors were not imported.`,
          success: (result) => {
            const skipped = Number(result.rows_failed || 0);
            return `Imported ${result.rows_processed || 0} valid rows. Leads: ${result.leads_created || 0} created, ${result.leads_updated || 0} updated, ${result.leads_reused || 0} unchanged. Companies: ${result.companies_created || 0} created, ${result.companies_updated || 0} updated, ${result.companies_reused || 0} unchanged. Contacts: ${result.contacts_created || 0} created, ${result.contacts_updated || 0} updated, ${result.contacts_reused || 0} unchanged.${skipped ? ` ${skipped} rows were skipped with errors.` : ""}`;
          },
          sheetName: "Leads import",
          exportSheetName: "Leads bulk update",
          exportFileName: "crm-leads-bulk-update.xlsx",
          guideSheetName: "Column guide",
          rulesSheetName: "Rules",
          rulesTitle: "Import rules",
          rules: {
            leadTitle: "lead.title is optional. When it is blank, CRM generates it from company, contact or source.",
            company: "company.name is required only when creating a new company; tax ID, email or name can reuse existing companies.",
            contact: "To create a contact, fill at least first name, last name, email or phone. Email, phone or document number can reuse existing contacts.",
            existingIds: "Use lead.id for exact lead updates. Without it, CRM updates a unique matching lead by title plus contact/company. Use lead.company_id, contact.company_id or lead.contact_customer_id only for existing IDs in this business unit.",
            ownerUpdate: "To reassign exported leads, fill lead.new_owner_user_id. Leave it blank to keep the current owner.",
            dates: "Date-only fields must use YYYY-MM-DD.",
            upload: "CSV, XLS and XLSX uploads use the first sheet and one row per lead.",
          },
          requirements: {
            required: "Required",
            conditional: "Conditional",
            optional: "Optional",
          },
          guideHeaders: {
            column: "Column",
            group: "Group",
            requirement: "Requirement",
            notes: "Notes",
            example: "Example",
          },
        },
        stageLegendTitle: "Stage guide",
        stageLegend: {
          new: "First signal of interest. Use it when the opportunity is not validated yet.",
          qualified: "There is a real need, budget or decision process worth following.",
          proposal: "A demo, quote, commercial proposal or concrete offer is in progress.",
          won: "The customer accepted or the deal is ready to activate.",
          lost: "The opportunity is closed without sale or no longer active.",
        },
        statuses: {
          open: "Open",
          inactive: "Inactive",
          won: "Won",
          lost: "Lost",
        },
        table: {
          title: "Lead",
          stage: "Stage",
          contact: "Contact",
          company: "Company",
          owner: "Owner",
          value: "Value",
          expectedClose: "Expected close",
          actions: "Actions",
        },
        fields: {
          title: "Lead title",
          stage: "Stage",
          status: "Status",
          contact_customer_id: "Contact",
          company_id: "Company",
          estimated_value: "Estimated value",
          expected_close_date: "Expected close date",
          source: "Source",
          notes: "Notes",
        },
        pagination: {
          refresh: "Refresh",
          previous: "Previous",
          next: "Next",
          page: (page) => `Page ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} of ${total} leads` : "0 leads",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Lead actions",
          noneSelected: "No lead selected",
          selected: (count) => `${count} selected`,
          selectLead: (title) => `Select ${title}`,
          selectVisible: "Select visible leads",
          singleOnly: "Select one lead for this action",
          workPermission: "You cannot work on this lead",
          assignPermission: "You cannot assign every selected lead",
          mergeMinimum: "Select at least two leads to merge",
          mergePermission: "You cannot merge every selected lead",
          convertOpportunity: "Convert to opportunity",
          convertOpportunityUnavailable: "Only new leads that are not already opportunities can be converted",
          convertOpportunityError: "Could not convert lead to opportunity.",
          call: "Call",
          merge: "Merge",
          clear: "Clear",
        },
        merge: {
          title: "Merge leads",
          subtitle: (count) => `${count} leads selected`,
          help: "Choose the lead that will remain visible. Empty fields, activity and tasks move into it. The absorbed leads are hidden and can be restored with unmerge.",
          targetLabel: "Final lead",
          targetHint: (id) => `Lead ID ${id}`,
          finalRecord: "Final lead",
          absorbedRecords: "Absorbed leads",
          confirm: "Merge leads",
          saving: "Merging",
          cancel: "Cancel",
          error: "Could not merge leads.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "Could not unmerge lead.",
          historyTitle: "Merge history",
          noHistory: "No merge history yet.",
        },
        assignment: {
          owner: "Owner",
          currentOwner: "Current owner",
          unassigned: "Unassigned",
          assign: "Assign",
          change: "Change owner",
          assignTitle: "Assign lead",
          assignMultipleTitle: "Assign selected leads",
          changeTitle: "Change lead owner",
          leads: "Leads",
          selectedLeads: (count) => `${count} selected leads`,
          selectUser: "Select a user",
          save: "Save owner",
          assignError: "Could not assign lead.",
          history: "Assignment history",
          historyError: "Could not load assignment history.",
          noHistory: "No assignment changes yet.",
          from: "From",
          by: "By",
        },
      },
      activity: {
        newTitle: "New conversation",
        addMessage: "Add message",
        newTask: "New follow-up",
        conversationTitle: "Conversation",
        empty: "No conversations found.",
        noConversationSelected: "Select a conversation to see the history.",
        loadError: "Could not load conversations.",
        saveError: "Could not save conversation.",
        messageSaveError: "Could not save message.",
        whatsappSend: "Send WhatsApp",
        whatsappSending: "Sending",
        whatsappPlaceholder: "Write a WhatsApp reply...",
        whatsappSent: "WhatsApp message sent.",
        whatsappSendError: "Could not send WhatsApp message.",
        whatsappTemplate: "Template",
        whatsappTemplateSend: "Send template",
        whatsappTemplateSent: "WhatsApp template sent.",
        whatsappTemplateSendError: "Could not send WhatsApp template.",
        whatsappTemplateName: "Template name",
        whatsappTemplateLanguage: "Language code",
        whatsappTemplateParameters: "Parameters, one per line",
        attachmentLabel: "Attachment",
        openAttachment: "Open attachment",
        linkContactError: "Could not link contact.",
        createContactError: "Could not create contact from this call.",
        searchPlaceholder: "Search conversations by subject or provider...",
        aiReady: "AI summaries will appear here when generated.",
        aiSummaryTitle: "AI summary",
        generateSummary: "Generate summary",
        generatingSummary: "Generating",
        summaryGenerated: "AI summary generated.",
        summaryGenerateError: "Could not generate AI summary.",
        nextStepsLabel: "Next steps",
        sentimentLabel: "Sentiment",
        contactLabel: "Contact",
        contactLinked: "Linked contact",
        noLinkedContactHint: "Calls will link automatically when a matching contact phone exists.",
        createContact: "Create contact",
        createContactTitle: "Create contact from call",
        createContactFromCall: "Create contact",
        createContactSuccess: "Contact created. You can link it from this conversation.",
        createContactLinkSuccess: "Contact created and linked to this conversation.",
        linkMatchingContact: "Link matching contact",
        linkingContact: "Linking contact",
        linkContactSuccess: "Contact linked to this conversation.",
        alreadyLinkedContact: "This conversation already has a linked contact.",
        noMatchingContact: "No matching contact found for this phone.",
        table: {
          subject: "Conversation",
          channel: "Channel",
          relatedTo: "Related to",
          status: "Status",
          updated: "Updated",
        },
        fields: {
          subject: "Subject",
          channel: "Channel",
          contact_customer_id: "Contact",
          company_id: "Company",
          lead_id: "Lead",
          status: "Status",
          provider: "Provider",
          direction: "Direction",
          message_type: "Type",
          body: "Message",
        },
        status: {
          open: "Open",
          closed: "Closed",
        },
      },
      tasks: {
        newTitle: "New task",
        editTitle: "Edit task",
        complete: "Complete",
        searchPlaceholder: "Search tasks by title, description or type...",
        empty: "No tasks found.",
        loadError: "Could not load tasks.",
        saveError: "Could not save task.",
        filterOpen: "Open tasks",
        filterAll: "All tasks",
        dueLabel: "Due",
        calendar: {
          viewMode: "Task view",
          list: "List",
          calendar: "Calendar",
          scope: "Scope",
          mine: "Mine",
          all: "All",
          view: "Calendar view",
          day: "Day",
          week: "Week",
          month: "Month",
          today: "Today",
          previous: "Previous",
          next: "Next",
          jumpToDate: "Jump to date",
          clickHint: "Click any date or hour to create a scheduled task.",
          empty: "No scheduled tasks in this view.",
          newAt: "New task at",
          newOn: "New task on",
          more: (count) => `+${count} more`,
          summary: (count) => `${count} task${Number(count) === 1 ? "" : "s"}`,
        },
        table: {
          title: "Task",
          type: "Type",
          relatedTo: "Related to",
          priority: "Priority",
          dueAt: "Due date",
          status: "Status",
          actions: "Actions",
        },
        fields: {
          title: "Title",
          description: "Description",
          task_type: "Type",
          priority: "Priority",
          status: "Status",
          due_at: "Due date",
          reminder_at: "Reminder",
          conversation_id: "Conversation",
          lead_id: "Lead",
          contact_customer_id: "Contact",
          company_id: "Company",
        },
      },
      companies: {
        newTitle: "New company",
        editTitle: "Edit company",
        searchPlaceholder: "Search companies by name, tax ID, email or industry...",
        empty: "No companies found.",
        loadError: "Could not load companies.",
        saveError: "Could not save company.",
        table: {
          name: "Company",
          email: "Email",
          phone: "Phone",
          industry: "Industry",
          status: "Status",
          contacts: "Contacts",
          actions: "Actions",
        },
        fields: {
          name: "Company name",
          legal_name: "Legal name",
          tax_id: "Tax ID",
          website: "Website",
          email: "Email",
          phone: "Phone",
          industry: "Industry",
          company_type: "Company type",
          status: "Status",
          address: "Address",
          city: "City",
          country: "Country",
          postal_code: "Postal code",
          notes: "Notes",
        },
        detail: {
          title: "Company detail",
          view: "View company",
          viewContacts: (name) => `View contacts for ${name || "company"}`,
          loadError: "Could not load company.",
          untitled: "Untitled company",
          contacts: "Contacts",
          emptyActivity: "No activity for this company yet.",
          emptyContacts: "No contacts linked to this company.",
          emptyLeads: "No leads linked to this company yet.",
          emptyOpportunities: "No opportunities linked to this company yet.",
          emptyTasks: "No tasks for this company yet.",
          noDueDate: "No due date",
          noNotes: "No notes yet.",
          tabsLabel: "Company detail tabs",
          tabs: {
            overview: "Overview",
            leads: "Leads",
            opportunities: "Opportunities",
            activity: "Activity",
            tasks: "Tasks",
          },
          addContact: "Add contact",
          createContactError: "Could not create contact for this company.",
          unassignContact: "Unassign",
          deactivateContact: "Deactivate",
          unassignContactError: "Could not unassign contact from company.",
          deactivateContactError: "Could not deactivate contact.",
        },
        pagination: {
          previous: "Previous",
          next: "Next",
          page: (page) => `Page ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} of ${total} companies` : "0 companies",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Company actions",
          noneSelected: "No company selected",
          selected: (count) => `${count} selected`,
          selectCompany: (name) => `Select ${name}`,
          selectVisible: "Select visible companies",
          singleOnly: "Select one company for this action",
          mergeMinimum: "Select at least two companies to merge",
          merge: "Merge",
          clear: "Clear",
        },
        merge: {
          title: "Merge companies",
          subtitle: (count) => `${count} companies selected`,
          help: "Choose the company that will remain visible. Empty fields, contacts, activity, tasks and linked leads move into it. The absorbed companies are hidden and can be restored with unmerge.",
          targetLabel: "Final company",
          targetHint: (id) => `Company ID ${id}`,
          finalRecord: "Final company",
          absorbedRecords: "Absorbed companies",
          confirm: "Merge companies",
          saving: "Merging",
          cancel: "Cancel",
          error: "Could not merge companies.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "Could not unmerge company.",
          historyTitle: "Merge history",
          noHistory: "No merge history yet.",
        },
      },
    },
  },
  es: {
    common: {
      crm: "CRM",
      pilcore: "Pilcore",
      modules: "Modulos",
      businessUnit: "Business Unit",
      logout: "Cerrar sesion",
      noRecords: "Sin registros",
      noLinkedContact: "Sin contacto vinculado",
      noMessages: "Todavia no hay historial",
      search: "Buscar",
      clear: "Limpiar",
      new: "Nuevo",
      edit: "Editar",
      view: "Ver",
      save: "Guardar",
      cancel: "Cancelar",
      saving: "Guardando",
      loading: "Cargando",
      optional: "Opcional",
      noCompany: "Sin empresa",
      noContact: "Sin contacto",
      noLead: "Sin lead",
      noConversation: "Sin conversacion",
      active: "Activo",
      inactive: "Inactivo",
      merge: "Merge",
      unmerge: "Unmerge",
      merged: "Merged",
    },
    conversationChannels: {
      manual: "Manual",
      call: "Llamada",
      whatsapp: "WhatsApp",
      email: "Email",
      meeting: "Reunion",
      note: "Nota",
    },
    conversationDirections: {
      internal: "Interno",
      incoming: "Entrante",
      outgoing: "Saliente",
    },
    conversationMessageTypes: {
      note: "Nota",
      text: "Texto",
      call: "Llamada",
      meeting: "Reunion",
      whatsapp: "WhatsApp",
      email: "Email",
    },
    callLog: {
      labels: {
        number: "Numero",
        extension: "Extension",
        result: "Resultado",
        duration: "Duracion",
        recordingId: "ID de grabacion",
      },
      events: {
        NOTIFY_START: "Llamada entrante iniciada",
        NOTIFY_INTERNAL: "Llamada entrante enviada a extension",
        NOTIFY_ANSWER: "Llamada contestada",
        NOTIFY_END: "Llamada entrante finalizada",
        NOTIFY_OUT_START: "Llamada saliente iniciada",
        NOTIFY_OUT_END: "Llamada saliente finalizada",
        NOTIFY_RECORD: "Grabacion de llamada lista",
        NOTIFY_IVR: "Opcion IVR seleccionada",
      },
      dispositions: {
        answered: "Contestada",
        busy: "Ocupado",
        cancel: "Cancelada",
        cancelled: "Cancelada",
        "no answer": "Sin respuesta",
        failed: "Fallida",
        "call failed": "Fallida",
        "no money": "Sin saldo",
        "unallocated number": "Numero no disponible",
        "no limit": "Limite alcanzado",
        "no day limit": "Limite diario alcanzado",
        "line limit": "Limite de lineas alcanzado",
        "no money, no limit": "Problema de saldo o limite",
      },
    },
    leadStages: {
      new: "Nuevo",
      qualified: "Cualificado",
      proposal: "Propuesta",
      won: "Ganado",
      lost: "Perdido",
    },
    taskTypes: {
      follow_up: "Seguimiento",
      call: "Llamada",
      email: "Email",
      whatsapp: "WhatsApp",
      meeting: "Reunion",
      todo: "Tarea",
    },
    taskPriorities: {
      low: "Baja",
      normal: "Normal",
      high: "Alta",
      urgent: "Urgente",
    },
    taskStatuses: {
      open: "Abierta",
      in_progress: "En progreso",
      done: "Completada",
      cancelled: "Cancelada",
    },
    shell: {
      navigationLabel: "Navegacion CRM",
      pinSidebar: "Fijar barra lateral",
      unpinSidebar: "Soltar barra lateral",
    },
    nav: {
      dashboard: { label: "Dashboard", meta: "Vista comercial" },
      contacts: { label: "Contactos", meta: "Personas y relaciones" },
      companies: { label: "Empresas", meta: "Cuentas y organizaciones" },
      leads: { label: "Leads", meta: "Prospectos y cualificacion" },
      opportunities: { label: "Oportunidades", meta: "Ventas y pipeline" },
      activity: { label: "Actividad", meta: "Llamadas, emails y notas" },
      tasks: { label: "Tareas", meta: "Seguimiento y responsables" },
      campaigns: { label: "Campanas", meta: "Segmentos y comunicacion" },
      loyalty: { label: "Fidelizacion", meta: "Valor y retencion" },
      reports: { label: "Informes", meta: "Rendimiento del CRM" },
      settings: { label: "Configuracion", meta: "Pipelines y preferencias" },
      userExperience: { label: "User Experience" },
      opportunityPipelines: { label: "Pipelines" },
    },
    metrics: {
      contacts: "Contactos",
      companies: "Empresas",
      leads: "Leads",
      opportunities: "Oportunidades",
      open_tasks: "Tareas abiertas",
      activities_this_week: "Actividad semanal",
    },
    dashboard: {
      title: "Dashboard",
      refresh: "Actualizar",
      loading: "Cargando",
      error: "No se pudo cargar el dashboard.",
      metricsAria: "Metricas CRM",
      pipelineKicker: "Pipeline",
      opportunitiesTitle: "Oportunidades",
      agendaKicker: "Agenda",
      tasksTitle: "Tareas",
      timelineKicker: "Timeline",
      recentActivityTitle: "Actividad reciente",
      totalValue: "Valor total",
      emptyTasks: "Sin tareas abiertas",
      emptyActivity: "Sin actividad registrada",
      dueAt: "Vence",
      openCount: (count) => `${count} abiertas`,
    },
    sections: {
      contacts: {
        title: "Contactos",
        columns: ["Nombre", "Empresa", "Email", "Telefono", "Estado"],
      },
      companies: {
        title: "Empresas",
        columns: ["Empresa", "Tipo", "Email", "Telefono", "Estado"],
      },
      leads: {
        title: "Leads",
        columns: ["Lead", "Origen", "Responsable", "Fecha", "Estado"],
      },
      opportunities: {
        title: "Oportunidades",
        columns: ["Oportunidad", "Cliente", "Etapa", "Valor", "Cierre"],
      },
      activity: {
        title: "Actividad",
        columns: ["Fecha", "Tipo", "Relacionado con", "Usuario", "Detalle"],
      },
      tasks: {
        title: "Tareas",
        columns: ["Tarea", "Relacionado con", "Vencimiento", "Responsable", "Estado"],
      },
      campaigns: {
        title: "Campanas",
        columns: ["Campana", "Segmento", "Inicio", "Fin", "Estado"],
      },
      loyalty: {
        title: "Fidelizacion",
        columns: ["Cliente", "Nivel", "Ultima estancia", "Valor", "Estado"],
      },
      reports: {
        title: "Informes",
        columns: ["Informe", "Periodo", "Responsable", "Actualizado", "Estado"],
      },
      settings: {
        title: "Configuracion",
        columns: ["Parametro", "Valor", "Actualizado"],
      },
    },
    entities: {
      contacts: {
        newTitle: "Nuevo contacto",
        editTitle: "Editar contacto",
        searchPlaceholder: "Buscar contactos por nombre, email, telefono o documento...",
        empty: "No se encontraron contactos.",
        loadError: "No se pudieron cargar los contactos.",
        saveError: "No se pudo guardar el contacto.",
        table: {
          name: "Nombre",
          company: "Empresa",
          email: "Email",
          phone: "Telefono",
          status: "Estado",
          actions: "Acciones",
        },
        fields: {
          first_name: "Nombre",
          last_name: "Apellido",
          email: "Email",
          phone: "Telefono",
          company_id: "Empresa",
          job_title: "Cargo",
          department: "Departamento",
          crm_status: "Estado CRM",
          source: "Origen",
          notes: "Notas",
        },
        pagination: {
          previous: "Anterior",
          next: "Siguiente",
          page: (page) => `Pagina ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} de ${total} contactos` : "0 contactos",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Acciones del contacto",
          noneSelected: "Ningun contacto seleccionado",
          selected: (count) => `${count} seleccionados`,
          selectContact: (name) => `Seleccionar ${name}`,
          selectVisible: "Seleccionar contactos visibles",
          singleOnly: "Selecciona un solo contacto para esta accion",
          mergeMinimum: "Selecciona al menos dos contactos para hacer merge",
          call: "Llamar",
          merge: "Merge",
          clear: "Limpiar",
        },
        merge: {
          title: "Merge de contactos",
          subtitle: (count) => `${count} contactos seleccionados`,
          help: "Elige el contacto que quedara visible. Los campos vacios, actividad, tareas y leads vinculados pasan a ese contacto. Los contactos absorbidos quedan ocultos y se pueden restaurar con unmerge.",
          targetLabel: "Contacto final",
          targetHint: (id) => `Contact ID ${id}`,
          finalRecord: "Contacto final",
          absorbedRecords: "Contactos absorbidos",
          confirm: "Merge contactos",
          saving: "Haciendo merge",
          cancel: "Cancelar",
          error: "No se pudo hacer merge de contactos.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "No se pudo deshacer el merge del contacto.",
          historyTitle: "Historico de merge",
          noHistory: "Todavia no hay historico de merge.",
        },
        detail: {
          viewDetails: "Ver contacto",
          backToContacts: "Contactos",
          refresh: "Actualizar",
          loadError: "No se pudo cargar el contacto.",
          untitledContact: "Contacto sin nombre",
          noEmail: "Sin email",
          noPhone: "Sin telefono",
          noNotes: "Todavia no hay notas.",
          noDueDate: "Sin vencimiento",
          emptyActivity: "Todavia no hay actividad para este contacto.",
          emptyLeads: "Todavia no hay leads vinculados a este contacto.",
          emptyOpportunities: "Todavia no hay oportunidades vinculadas a este contacto.",
          emptyTasks: "Todavia no hay tareas para este contacto.",
          tabsLabel: "Pestanas de detalle del contacto",
          tabs: {
            overview: "Resumen",
            leads: "Leads",
            opportunities: "Oportunidades",
            activity: "Actividad",
            tasks: "Tareas",
          },
          metrics: {
            activity: "Actividad",
            openTasks: "Tareas abiertas",
            lastContact: "Ultimo contacto",
          },
          sections: {
            contact: "Informacion del contacto",
            company: "Empresa",
            notes: "Notas",
          },
          fields: {
            lastContacted: "Ultimo contacto",
          },
          unmerge: "Unmerge contacto",
        },
      },
      leads: {
        newTitle: "Nuevo lead",
        editTitle: "Editar lead",
        searchPlaceholder: "Buscar leads por titulo, origen o notas...",
        empty: "No se encontraron leads.",
        opportunities: {
          newTitle: "Nueva oportunidad",
          editTitle: "Editar oportunidad",
          view: "Ver oportunidad",
          detailTitle: "Detalle de oportunidad",
          detailLoadError: "No se pudo cargar el detalle de la oportunidad.",
          noItem: "Ninguna oportunidad",
          fieldTitle: "Titulo de la oportunidad",
          tableTitle: "Oportunidad",
          searchPlaceholder: "Buscar oportunidades por titulo, origen o notas...",
          empty: "No se encontraron oportunidades.",
          selectionTitle: "Acciones de oportunidad",
          noneSelected: "Ninguna oportunidad seleccionada",
          selectItem: (title) => `Seleccionar ${title}`,
          singleOnly: "Selecciona una sola oportunidad para esta accion",
          workPermission: "No puedes trabajar sobre esta oportunidad",
          assignPermission: "No puedes asignar todas las oportunidades seleccionadas",
          mergeMinimum: "Selecciona al menos dos oportunidades para hacer merge",
          mergePermission: "No puedes hacer merge de todas las oportunidades seleccionadas",
          assignmentChange: "Cambiar responsable",
          assignmentAssignTitle: "Asignar oportunidad",
          assignmentMultipleTitle: "Asignar oportunidades seleccionadas",
          assignmentChangeTitle: "Cambiar responsable de la oportunidad",
          assignmentItems: "Oportunidades",
          selectedItems: (count) => `${count} oportunidades seleccionadas`,
          assignmentError: "No se pudo asignar la oportunidad.",
          history: "Historico de asignaciones",
          historyError: "No se pudo cargar el historico de asignaciones.",
          tableView: "Tabla",
          kanbanView: "Pipeline",
          pipeline: "Pipeline",
          pipelineStage: "Etapa de pipeline",
          noPipeline: "Sin pipeline",
          allPipelines: "Todas las pipelines",
          pipelineLoadError: "No se pudieron cargar las pipelines de oportunidades.",
          pipelineEmpty: "No hay etapas activas en esta pipeline.",
          pipelineUnassignedTitle: "Sin pipeline",
          moveToStage: "Mover a etapa",
          noOpportunitiesInStage: "Sin oportunidades aqui",
          paginationSummary: (start, end, total) =>
            total > 0 ? `${start}-${end} de ${total} oportunidades` : "0 oportunidades",
          merge: {
            title: "Merge de oportunidades",
            subtitle: (count) => `${count} oportunidades seleccionadas`,
            help: "Elige la oportunidad que quedara visible. Los campos vacios, actividad y tareas pasan a esa oportunidad. Las oportunidades absorbidas quedan ocultas y se pueden restaurar con unmerge.",
            targetLabel: "Oportunidad final",
            targetHint: (id) => `Opportunity ID ${id}`,
            finalRecord: "Oportunidad final",
            absorbedRecords: "Oportunidades absorbidas",
            confirm: "Merge oportunidades",
            saving: "Haciendo merge",
            cancel: "Cancelar",
            error: "No se pudo hacer merge de oportunidades.",
            unmerge: "Unmerge",
            merged: "Merged",
            unmerged: "Unmerged",
            unmergeError: "No se pudo deshacer el merge de la oportunidad.",
            historyTitle: "Historico de merge",
            noHistory: "Todavia no hay historico de merge.",
          },
        },
        pipeline: {
          tableView: "Tabla",
          kanbanView: "Pipeline",
          pipeline: "Pipeline",
          pipelineStage: "Etapa de pipeline",
          noPipeline: "Sin pipeline",
          allPipelines: "Todas las pipelines",
          pipelineLoadError: "No se pudieron cargar las pipelines.",
          pipelineEmpty: "No hay etapas activas en esta pipeline.",
          pipelineUnassignedTitle: "Sin pipeline",
          moveToStage: "Mover a etapa",
          noItemsInStage: "Sin leads aqui",
          itemCount: (count) => `${count} lead${Number(count) === 1 ? "" : "s"}`,
        },
        loadError: "No se pudieron cargar los leads.",
        saveError: "No se pudo guardar el lead.",
        createContactError: "No se pudo crear el contacto.",
        createCompanyError: "No se pudo crear la empresa.",
        addContactOption: "+ Nuevo contacto",
        addCompanyOption: "+ Nueva empresa",
        relationship: {
          title: "Relacionar contacto y empresa?",
          message: (contact, company) =>
            `${contact} no esta vinculado a ${company}. Quieres relacionarlos ahora?`,
          currentCompany: (company) => `Empresa actual del contacto: ${company}.`,
          confirm: "Relacionarlos",
          cancel: "Cancelar",
          saving: "Relacionando",
          error: "No se pudo relacionar el contacto con la empresa.",
        },
        detail: {
          view: "Ver lead",
          title: "Detalle del lead",
          loadError: "No se pudo cargar el detalle del lead.",
          contacts: "Contactos",
          companies: "Empresas",
          noContacts: "No hay contactos asignados.",
          noCompanies: "No hay empresas asignadas.",
          noSource: "Sin origen",
          updated: "Actualizado",
          primaryContact: "Contacto del lead",
          companyContact: "Contacto de empresa",
          removeContact: "Desasignar contacto",
          removeCompany: "Desasignar empresa",
          removeContactError: "No se pudo desasignar el contacto.",
          removeCompanyError: "No se pudo desasignar la empresa.",
          companyContacts: (count) => `${count} contacto${Number(count) === 1 ? "" : "s"}`,
        },
        import: {
          bulkOptions: "Carga masiva",
          downloadTemplate: "Plantilla vacia",
          uploadFile: "Importar / previsualizar",
          exportLeads: "Exportar leads Excel",
          exporting: "Exportando",
          previewing: "Previsualizando",
          importing: "Importando",
          error: "No se pudo importar el archivo.",
          exportError: "No se pudieron exportar los leads.",
          emptyFile: "El archivo seleccionado no contiene filas con datos.",
          unknownColumns: (columns) => `Columnas desconocidas: ${columns}. Usa los encabezados de la plantilla.`,
          rowError: (row, column, message) => `Fila ${row}, ${column}: ${message}`,
          actions: {
            created: "se creara",
            updated: "se actualizara",
            reused: "no cambia",
            unknown: "se revisara",
          },
          previewTitle: "Previsualizacion de importacion",
          previewSummary: (result) =>
            `${result.rows_processed || 0} listas, ${result.rows_failed || 0} con errores, ${result.total_rows || 0} en total.`,
          previewRowsTitle: "Filas listas",
          previewErrorsTitle: "Filas a corregir",
          previewRow: (item, action) =>
            `Fila ${item.row}: lead ${action}${item.lead_title ? ` - ${item.lead_title}` : ""}`,
          previewMeta: (companyAction, contactAction) =>
            `Empresa ${companyAction}; contacto ${contactAction}.`,
          previewError: (item) => `Fila ${item.row}, ${item.column}: ${item.message}`,
          previewEmptyRows: "No hay filas listas para importar.",
          previewNoErrors: "No se encontraron errores por fila.",
          confirmImport: "Importar filas validas",
          cancelPreview: "Cancelar",
          moreRows: (count) => `+ ${count} filas listas mas`,
          moreErrors: (count) => `+ ${count} errores mas`,
          resultErrorsTitle: "Filas pendientes de corregir",
          resultErrorsSummary: (count) => `${count} errores de fila no se importaron.`,
          success: (result) => {
            const skipped = Number(result.rows_failed || 0);
            return `Importadas ${result.rows_processed || 0} filas validas. Leads: ${result.leads_created || 0} creados, ${result.leads_updated || 0} actualizados, ${result.leads_reused || 0} sin cambios. Empresas: ${result.companies_created || 0} creadas, ${result.companies_updated || 0} actualizadas, ${result.companies_reused || 0} sin cambios. Contactos: ${result.contacts_created || 0} creados, ${result.contacts_updated || 0} actualizados, ${result.contacts_reused || 0} sin cambios.${skipped ? ` ${skipped} filas se omitieron por errores.` : ""}`;
          },
          sheetName: "Importar leads",
          exportSheetName: "Actualizacion masiva",
          exportFileName: "crm-leads-actualizacion-masiva.xlsx",
          guideSheetName: "Guia columnas",
          rulesSheetName: "Reglas",
          rulesTitle: "Reglas de importacion",
          rules: {
            leadTitle: "lead.title es opcional. Si viene vacio, CRM lo genera desde empresa, contacto u origen.",
            company: "company.name solo es obligatorio al crear una empresa nueva; NIF, email o nombre pueden reutilizar empresas existentes.",
            contact: "Para crear un contacto, rellena al menos nombre, apellido, email o telefono. Email, telefono o documento pueden reutilizar contactos existentes.",
            existingIds: "Usa lead.id para actualizaciones exactas. Sin el, CRM actualiza un lead unico coincidente por titulo mas contacto/empresa. Usa lead.company_id, contact.company_id o lead.contact_customer_id solo para IDs existentes en esta business unit.",
            ownerUpdate: "Para reasignar leads exportados, rellena lead.new_owner_user_id. Si queda vacio, conserva el responsable actual.",
            dates: "Los campos de fecha deben usar YYYY-MM-DD.",
            upload: "Las subidas CSV, XLS y XLSX usan la primera hoja y una fila por lead.",
          },
          requirements: {
            required: "Obligatorio",
            conditional: "Condicional",
            optional: "Opcional",
          },
          guideHeaders: {
            column: "Columna",
            group: "Grupo",
            requirement: "Requisito",
            notes: "Notas",
            example: "Ejemplo",
          },
        },
        stageLegendTitle: "Guia de etapas",
        stageLegend: {
          new: "Primera senal de interes. Usalo cuando la oportunidad aun no esta validada.",
          qualified: "Hay una necesidad real, presupuesto o proceso de decision que merece seguimiento.",
          proposal: "Hay demo, presupuesto, propuesta comercial u oferta concreta en marcha.",
          won: "El cliente ha aceptado o la venta esta lista para activar.",
          lost: "La oportunidad se ha cerrado sin venta o ya no esta activa.",
        },
        statuses: {
          open: "Abierto",
          inactive: "Inactivo",
          won: "Ganado",
          lost: "Perdido",
        },
        table: {
          title: "Lead",
          stage: "Etapa",
          contact: "Contacto",
          company: "Empresa",
          owner: "Responsable",
          value: "Valor",
          expectedClose: "Cierre previsto",
          actions: "Acciones",
        },
        fields: {
          title: "Titulo del lead",
          stage: "Etapa",
          status: "Estado",
          contact_customer_id: "Contacto",
          company_id: "Empresa",
          estimated_value: "Valor estimado",
          expected_close_date: "Fecha de cierre prevista",
          source: "Origen",
          notes: "Notas",
        },
        pagination: {
          refresh: "Actualizar",
          previous: "Anterior",
          next: "Siguiente",
          page: (page) => `Pagina ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} de ${total} leads` : "0 leads",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Acciones del lead",
          noneSelected: "Ningun lead seleccionado",
          selected: (count) => `${count} seleccionados`,
          selectLead: (title) => `Seleccionar ${title}`,
          selectVisible: "Seleccionar leads visibles",
          singleOnly: "Selecciona un solo lead para esta accion",
          workPermission: "No puedes trabajar sobre este lead",
          assignPermission: "No puedes asignar todos los leads seleccionados",
          mergeMinimum: "Selecciona al menos dos leads para hacer merge",
          mergePermission: "No puedes hacer merge de todos los leads seleccionados",
          convertOpportunity: "Pasar a oportunidad",
          convertOpportunityUnavailable: "Solo se pueden convertir leads nuevos que aun no son oportunidades",
          convertOpportunityError: "No se pudo pasar el lead a oportunidad.",
          call: "Llamar",
          merge: "Merge",
          clear: "Limpiar",
        },
        merge: {
          title: "Merge de leads",
          subtitle: (count) => `${count} leads seleccionados`,
          help: "Elige el lead que quedara visible. Los campos vacios, actividad y tareas pasan a ese lead. Los leads absorbidos quedan ocultos y se pueden restaurar con unmerge.",
          targetLabel: "Lead final",
          targetHint: (id) => `Lead ID ${id}`,
          finalRecord: "Lead final",
          absorbedRecords: "Leads absorbidos",
          confirm: "Merge leads",
          saving: "Haciendo merge",
          cancel: "Cancelar",
          error: "No se pudo hacer merge de leads.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "No se pudo deshacer el merge del lead.",
          historyTitle: "Historico de merge",
          noHistory: "Todavia no hay historico de merge.",
        },
        assignment: {
          owner: "Responsable",
          currentOwner: "Responsable actual",
          unassigned: "Sin asignar",
          assign: "Asignar",
          change: "Cambiar responsable",
          assignTitle: "Asignar lead",
          assignMultipleTitle: "Asignar leads seleccionados",
          changeTitle: "Cambiar responsable del lead",
          leads: "Leads",
          selectedLeads: (count) => `${count} leads seleccionados`,
          selectUser: "Selecciona un usuario",
          save: "Guardar responsable",
          assignError: "No se pudo asignar el lead.",
          history: "Historico de asignaciones",
          historyError: "No se pudo cargar el historico de asignaciones.",
          noHistory: "Todavia no hay cambios de responsable.",
          from: "Desde",
          by: "Por",
        },
      },
      activity: {
        newTitle: "Nueva conversacion",
        addMessage: "Anadir mensaje",
        newTask: "Nuevo seguimiento",
        conversationTitle: "Conversacion",
        empty: "No se encontraron conversaciones.",
        noConversationSelected: "Selecciona una conversacion para ver el historial.",
        loadError: "No se pudieron cargar las conversaciones.",
        saveError: "No se pudo guardar la conversacion.",
        messageSaveError: "No se pudo guardar el mensaje.",
        whatsappSend: "Enviar WhatsApp",
        whatsappSending: "Enviando",
        whatsappPlaceholder: "Escribe una respuesta de WhatsApp...",
        whatsappSent: "Mensaje de WhatsApp enviado.",
        whatsappSendError: "No se pudo enviar el mensaje de WhatsApp.",
        whatsappTemplate: "Plantilla",
        whatsappTemplateSend: "Enviar plantilla",
        whatsappTemplateSent: "Plantilla de WhatsApp enviada.",
        whatsappTemplateSendError: "No se pudo enviar la plantilla de WhatsApp.",
        whatsappTemplateName: "Nombre de plantilla",
        whatsappTemplateLanguage: "Codigo de idioma",
        whatsappTemplateParameters: "Parametros, uno por linea",
        attachmentLabel: "Adjunto",
        openAttachment: "Abrir adjunto",
        linkContactError: "No se pudo vincular el contacto.",
        createContactError: "No se pudo crear el contacto desde esta llamada.",
        searchPlaceholder: "Buscar conversaciones por asunto o proveedor...",
        aiReady: "Los resumenes AI apareceran aqui cuando se generen.",
        aiSummaryTitle: "Resumen AI",
        generateSummary: "Generar resumen",
        generatingSummary: "Generando",
        summaryGenerated: "Resumen AI generado.",
        summaryGenerateError: "No se pudo generar el resumen AI.",
        nextStepsLabel: "Siguientes pasos",
        sentimentLabel: "Sentimiento",
        contactLabel: "Contacto",
        contactLinked: "Contacto vinculado",
        noLinkedContactHint: "Las llamadas se vincularan automaticamente cuando exista un telefono coincidente.",
        createContact: "Crear contacto",
        createContactTitle: "Crear contacto desde llamada",
        createContactFromCall: "Crear contacto",
        createContactSuccess: "Contacto creado. Puedes vincularlo desde esta conversacion.",
        createContactLinkSuccess: "Contacto creado y vinculado a esta conversacion.",
        linkMatchingContact: "Vincular contacto coincidente",
        linkingContact: "Vinculando contacto",
        linkContactSuccess: "Contacto vinculado a esta conversacion.",
        alreadyLinkedContact: "Esta conversacion ya tiene un contacto vinculado.",
        noMatchingContact: "No se encontro un contacto coincidente para este telefono.",
        table: {
          subject: "Conversacion",
          channel: "Canal",
          relatedTo: "Relacionado con",
          status: "Estado",
          updated: "Actualizado",
        },
        fields: {
          subject: "Asunto",
          channel: "Canal",
          contact_customer_id: "Contacto",
          company_id: "Empresa",
          lead_id: "Lead",
          status: "Estado",
          provider: "Proveedor",
          direction: "Direccion",
          message_type: "Tipo",
          body: "Mensaje",
        },
        status: {
          open: "Abierta",
          closed: "Cerrada",
        },
      },
      tasks: {
        newTitle: "Nueva tarea",
        editTitle: "Editar tarea",
        complete: "Completar",
        searchPlaceholder: "Buscar tareas por titulo, descripcion o tipo...",
        empty: "No se encontraron tareas.",
        loadError: "No se pudieron cargar las tareas.",
        saveError: "No se pudo guardar la tarea.",
        filterOpen: "Tareas abiertas",
        filterAll: "Todas las tareas",
        dueLabel: "Vence",
        calendar: {
          viewMode: "Vista de tareas",
          list: "Lista",
          calendar: "Agenda",
          scope: "Alcance",
          mine: "Mias",
          all: "Todas",
          view: "Vista de agenda",
          day: "Dia",
          week: "Semana",
          month: "Mes",
          today: "Hoy",
          previous: "Anterior",
          next: "Siguiente",
          jumpToDate: "Ir a fecha",
          clickHint: "Haz clic en una fecha u hora para crear una tarea programada.",
          empty: "No hay tareas programadas en esta vista.",
          newAt: "Nueva tarea a las",
          newOn: "Nueva tarea el",
          more: (count) => `+${count} mas`,
          summary: (count) => `${count} tarea${Number(count) === 1 ? "" : "s"}`,
        },
        table: {
          title: "Tarea",
          type: "Tipo",
          relatedTo: "Relacionado con",
          priority: "Prioridad",
          dueAt: "Vencimiento",
          status: "Estado",
          actions: "Acciones",
        },
        fields: {
          title: "Titulo",
          description: "Descripcion",
          task_type: "Tipo",
          priority: "Prioridad",
          status: "Estado",
          due_at: "Vencimiento",
          reminder_at: "Recordatorio",
          conversation_id: "Conversacion",
          lead_id: "Lead",
          contact_customer_id: "Contacto",
          company_id: "Empresa",
        },
      },
      companies: {
        newTitle: "Nueva empresa",
        editTitle: "Editar empresa",
        searchPlaceholder: "Buscar empresas por nombre, NIF, email o industria...",
        empty: "No se encontraron empresas.",
        loadError: "No se pudieron cargar las empresas.",
        saveError: "No se pudo guardar la empresa.",
        table: {
          name: "Empresa",
          email: "Email",
          phone: "Telefono",
          industry: "Industria",
          status: "Estado",
          contacts: "Contactos",
          actions: "Acciones",
        },
        fields: {
          name: "Nombre de empresa",
          legal_name: "Razon social",
          tax_id: "NIF",
          website: "Web",
          email: "Email",
          phone: "Telefono",
          industry: "Industria",
          company_type: "Tipo de empresa",
          status: "Estado",
          address: "Direccion",
          city: "Ciudad",
          country: "Pais",
          postal_code: "Codigo postal",
          notes: "Notas",
        },
        detail: {
          title: "Detalle de empresa",
          view: "Ver empresa",
          viewContacts: (name) => `Ver contactos de ${name || "la empresa"}`,
          loadError: "No se pudo cargar la empresa.",
          untitled: "Empresa sin nombre",
          contacts: "Contactos",
          emptyActivity: "Todavia no hay actividad para esta empresa.",
          emptyContacts: "No hay contactos vinculados a esta empresa.",
          emptyLeads: "Todavia no hay leads vinculados a esta empresa.",
          emptyOpportunities: "Todavia no hay oportunidades vinculadas a esta empresa.",
          emptyTasks: "Todavia no hay tareas para esta empresa.",
          noDueDate: "Sin vencimiento",
          noNotes: "Todavia no hay notas.",
          tabsLabel: "Pestanas de detalle de empresa",
          tabs: {
            overview: "Resumen",
            leads: "Leads",
            opportunities: "Oportunidades",
            activity: "Actividad",
            tasks: "Tareas",
          },
          addContact: "Añadir contacto",
          createContactError: "No se pudo crear el contacto para esta empresa.",
          unassignContact: "Quitar de empresa",
          deactivateContact: "Desactivar",
          unassignContactError: "No se pudo quitar el contacto de la empresa.",
          deactivateContactError: "No se pudo desactivar el contacto.",
        },
        pagination: {
          previous: "Anterior",
          next: "Siguiente",
          page: (page) => `Pagina ${page}`,
          summary: (start, end, total) =>
            total > 0 ? `${start}-${end} de ${total} empresas` : "0 empresas",
          showInactive: "Show inactive",
          hideInactive: "Hide inactive",
        },
        selection: {
          title: "Acciones de empresa",
          noneSelected: "Ninguna empresa seleccionada",
          selected: (count) => `${count} seleccionadas`,
          selectCompany: (name) => `Seleccionar ${name}`,
          selectVisible: "Seleccionar empresas visibles",
          singleOnly: "Selecciona una sola empresa para esta accion",
          mergeMinimum: "Selecciona al menos dos empresas para hacer merge",
          merge: "Merge",
          clear: "Limpiar",
        },
        merge: {
          title: "Merge de empresas",
          subtitle: (count) => `${count} empresas seleccionadas`,
          help: "Elige la empresa que quedara visible. Los campos vacios, contactos, actividad, tareas y leads vinculados pasan a esa empresa. Las empresas absorbidas quedan ocultas y se pueden restaurar con unmerge.",
          targetLabel: "Empresa final",
          targetHint: (id) => `Company ID ${id}`,
          finalRecord: "Empresa final",
          absorbedRecords: "Empresas absorbidas",
          confirm: "Merge empresas",
          saving: "Haciendo merge",
          cancel: "Cancelar",
          error: "No se pudo hacer merge de empresas.",
          unmerge: "Unmerge",
          merged: "Merged",
          unmerged: "Unmerged",
          unmergeError: "No se pudo deshacer el merge de la empresa.",
          historyTitle: "Historico de merge",
          noHistory: "Todavia no hay historico de merge.",
        },
      },
    },
  },
};

export function getCrmCopy(languageId) {
  return CRM_COPY[languageId] || CRM_COPY[CRM_DEFAULT_LANGUAGE_ID];
}

export function useCrmCopy() {
  const { languageId } = usePmsTheme();

  return useMemo(() => getCrmCopy(languageId), [languageId]);
}
